import React, { useMemo, useCallback, createRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Grid, Responsive } from 'vendors/semantic-ui';

import { configUI } from 'config';
import { removeAccent } from 'modules/shared/utils';

import { Brand } from '../../models';
import { getBrandUrl } from '../../utils';

import { BrandsProps, AlphabeticalBrandSection } from './brands-type';

import enhancer from './brands-enhancer';

import styles from './brands-page.module.css';

function chunk<T>(array: T[], parts: number): T[][] {
    const arr = [...array];
    const result = [];
    for (let i = parts; i > 0; i -= 1) {
        result.push(arr.splice(0, Math.ceil(arr.length / i)));
    }
    return result;
}

const getAlphabeticalBrandSections = (brands: Brand[]): AlphabeticalBrandSection[] => {
    if (brands.length === 0) {
        return [];
    }

    const res = Object.values<AlphabeticalBrandSection>(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        brands.reduce((acc: any, brand) => {
            // -- on recupère la première lettre sans accents en majuscule
            let firstLetter = removeAccent(brand.name[0]).toUpperCase();

            // -- si la première lettre n'est pas une lettre on les regroupe dans '_' (apres A-Z dans la table ASCII pour le sort)
            if (!/^[a-zA-Z]$/.test(firstLetter)) {
                firstLetter = '_';
            }

            if (!acc[firstLetter]) {
                acc[firstLetter] = { title: firstLetter, data: [brand] };
            } else {
                acc[firstLetter].data.push(brand);
            }

            return acc;
        }, {}),
    );

    return res
        .sort((a, b): number => {
            if (a.title > b.title) {
                return 1;
            }
            if (b.title > a.title) {
                return -1;
            }
            return 0;
        })
        .map(item => {
            const data = item.data.sort();
            return {
                title: item.title.replace('_', '#'), // -- on remplace _ par # pour l'affichage
                data,
                chunkData: chunk(data, 4), // -- on découpe les marques en 4 parties
            };
        });
};

const scrollToRef = (ref: React.RefObject<HTMLDivElement>): void => {
    if (ref && ref.current) {
        window.scrollTo(0, ref.current.offsetTop);
    }
};

const InnerBrandsPage: React.FC<BrandsProps> = React.memo(({ brands }: BrandsProps) => {
    if (!brands) return <React.Fragment />;

    const sections = useMemo(() => getAlphabeticalBrandSections(brands), [brands]);

    const sectionRefs = useMemo(() => sections.map(() => createRef<HTMLDivElement>()), [sections]);

    const handleLetterClick = useCallback(
        (index: number) => {
            scrollToRef(sectionRefs[index]);
        },
        [sectionRefs],
    );

    const renderBrands = (brandsPart: Brand[]): JSX.Element[] => {
        return brandsPart.map(item => {
            return (
                <React.Fragment key={item.id}>
                    <Link
                        to={getBrandUrl(item)}
                        className={`${styles.brandBtn} ${!item.brandCornerAvailable ? '' : styles.bold}`}
                    >
                        {item.name.toUpperCase()}
                    </Link>
                    <br />
                </React.Fragment>
            );
        });
    };

    const renderSection = (section: AlphabeticalBrandSection, index: number): JSX.Element => {
        return (
            <div key={section.title} ref={sectionRefs[index]} className={styles.section}>
                <Grid padded container stackable centered>
                    <Grid.Column width={2} textAlign="left">
                        <span className={styles.sectionTitle}>{section.title}</span>
                    </Grid.Column>
                    <Responsive as={React.Fragment} minWidth={configUI.responsive.tablet.minWidth}>
                        <Grid.Column width={3} textAlign="left">
                            {renderBrands(section.chunkData[0])}
                        </Grid.Column>
                        <Grid.Column width={3} textAlign="left">
                            {renderBrands(section.chunkData[1])}
                        </Grid.Column>
                        <Grid.Column width={3} textAlign="left">
                            {renderBrands(section.chunkData[2])}
                        </Grid.Column>
                        <Grid.Column width={3} textAlign="left">
                            {renderBrands(section.chunkData[3])}
                        </Grid.Column>
                    </Responsive>
                    <Responsive as={React.Fragment} maxWidth={configUI.responsive.mobile.maxWidth}>
                        <Grid.Column width={14} textAlign="left">
                            {renderBrands(section.data)}
                        </Grid.Column>
                    </Responsive>
                </Grid>
            </div>
        );
    };
    return (
        <div className={styles.page}>
            <Container>
                <div className={styles.container}>
                    <div className={styles.summary}>
                        <div>NOS MARQUES DE A À Z</div>
                        <div className={styles.alphabeticalDesktop}>
                            {sections.map((item, index) => {
                                return (
                                    <button
                                        type="button"
                                        key={item.title}
                                        className={styles.letterBtn}
                                        onClick={() => handleLetterClick(index)}
                                    >
                                        &nbsp;&nbsp;{item.title}&nbsp;&nbsp;
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        {sections.map((item, index) => {
                            return renderSection(item, index);
                        })}
                    </div>
                </div>
            </Container>
            <div className={styles.alphabeticalMobile}>
                {sections.map((item, index) => {
                    return (
                        <div key={item.title}>
                            <button type="button" className={styles.letterBtn} onClick={() => handleLetterClick(index)}>
                                {item.title}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export const BrandsPage = enhancer(InnerBrandsPage);
